/**
 * Shared invoice line-item helpers used by both the Invoicing tab (InvoicingView)
 * and the Accessorial modal. Pure functions + the V1 GL-code option lists, kept in
 * one module so the two editors stay in lockstep. NO font-mono / no UI here.
 */

// ── rate types (V1 %ratetypemap, lib_invoice.pl:5314-5321) ────────────────────
export const RATE_TYPE_OPTIONS = ['DAYS', 'FLAT', 'HOURS', 'MILES', 'PCT %', 'GALLON'];

export const isPctRateType = (rt: string | null | undefined): boolean =>
  (rt || '').trim().toUpperCase() === 'PCT %';

/**
 * The line-item total, computed the way V1's invoice screen does (cms.js
 * invoice_calctotal):
 *   - PCT % rate type → (qty × rate) / 100
 *   - any other type  → qty × rate
 * Rounded with toFixed(2), so an exact half-cent rounds UP (245 × 52.50% = 128.63).
 * The backend's twin, app/utils/invoice_money.py `line_total`, reproduces this
 * rounding for every total the server writes or proposes (reconciler, accessorial
 * upload, Change All) — change one and the other has to follow (bugfix/#34232).
 * The Total column is read-only and is never hand-edited — this is its only source.
 */
export const computeLineTotal = (
  qty: number,
  rate: number,
  rateType: string,
): number => {
  const base = (qty || 0) * (rate || 0);
  const amount = isPctRateType(rateType) ? base / 100 : base;
  return Number(amount.toFixed(2));
};

// ── GL-code ("limastype") smart lists (V1 defs.pl:3006-3065) ──────────────────
// A fixed dropdown, NOT a glcodes FK. The list swaps based on the row's Item:
// the special item "DRAY/TRANSLOAD/DELIVERY" uses the DTD list (/WHSE + /REGION
// variants); every other item uses the "all" list.
export const DTD_ITEM_NAME = 'DRAY/TRANSLOAD/DELIVERY';

/** @invlimastypeall_data (defs.pl:3006), sorted — the default list. */
export const ALL_GL_CODES: readonly string[] = [
  'GWI05-06', 'GWI08A', 'GWI08B', 'GWI09-10', 'GWI11', 'GWI13', 'GWI14', 'GWI16',
  'GWI17', 'GWI18', 'GWI20', 'GWI22', 'GWI27', 'GWI28', 'GWIC', 'REGION', 'SINGLE',
].slice().sort();

// @invlimastypeDTD_data (defs.pl:3027) — each base × {/WHSE, /REGION}, sorted.
const _DTD_BASES = [
  'GWI04', 'GWI05-06', 'GWI08A', 'GWI08B', 'GWI09-10', 'GWI11', 'GWI12', 'GWI13',
  'GWI14', 'GWI15', 'GWI16', 'GWI17', 'GWI18', 'GWI20', 'GWI22', 'GWI27', 'GWI28', 'GWIC',
];
export const DTD_GL_CODES: readonly string[] = _DTD_BASES
  .flatMap((b) => [`${b}/WHSE`, `${b}/REGION`])
  .sort();

/** The GL-code options for a line, chosen by its Item (V1 lib_editrecord.pl:9543). */
export const glCodesForItem = (item: string | null | undefined): readonly string[] =>
  (item || '').trim().toUpperCase() === DTD_ITEM_NAME ? DTD_GL_CODES : ALL_GL_CODES;

/**
 * Red-flag a GL code that doesn't match the pro's receiving warehouses
 * (V1 lib_editrecord.pl:9574-9603). Only GWI* codes flag. The `/WHSE`/`/REGION`
 * suffix is stripped first; GWI05-06 matches GWI05|GWI06 and GWI09-10 matches
 * GWI09|GWI10; every other base is a substring match against the comma-joined
 * rcvWhseList. An empty rcvWhseList flags every GWI code (parity with V1).
 */
export const glCodeFlagged = (
  code: string | null | undefined,
  rcvWhseList: string | null | undefined,
): boolean => {
  const c = (code || '').trim();
  if (!/^GWI/i.test(c)) return false;
  const base = c.replace(/\/.*$/, ''); // strip /WHSE or /REGION
  const list = rcvWhseList || '';
  if (list === '') return true; // no receiving warehouses → flag all GWI codes
  let found: boolean;
  if (base === 'GWI05-06') found = /GWI05|GWI06/.test(list);
  else if (base === 'GWI09-10') found = /GWI09|GWI10/.test(list);
  else found = list.includes(base);
  return !found;
};

// ── Whse-tab warehouse rule (backend invoice_whse_gl.py — keep in lockstep) ──────
// A GWI-coded line should name a warehouse the pro worked through: the receiving rows
// (by pronumber or blpronumber) plus the BL rows the Whse tab shows, which the server
// hands over as `OrderDetails.whseCodes`.
//
// Two strengths (Lee, 2026-10-16). `whseGlCodeMatches` is the HIGHLIGHT: the Invoicing
// tab paints EVERY GWI-coded miss red on load, V1's advisory callout. `whseGlCodeErrors`
// is the BLOCK: only `WHSE_BLOCKING_ITEMS` refuse Pending Approval→RESOLVED, because a
// warehouse code on a GROUNDED / FUEL SURCHARGE / YARD STORAGE line routinely names a
// warehouse the move never touched — red there is informative, refusing the save is not.
//
// Differs from `glCodeFlagged` (the Accessorial modal's V1-parity flag) in two
// deliberate ways: whole-token match instead of substring, and an EMPTY list is
// "nothing to compare" rather than flag-everything.
const _WHSE_PAIRED: Record<string, readonly string[]> = {
  'GWI05-06': ['GWI05', 'GWI06'],
  'GWI09-10': ['GWI09', 'GWI10'],
};

/**
 * The only line items whose mismatch blocks Pending Approval→RESOLVED — the dray line
 * is the one whose code has to follow the move. Exact stored `liitem` spellings;
 * mirrors `invoice_whse_gl.BLOCKING_ITEMS`.
 */
export const WHSE_BLOCKING_ITEMS: readonly string[] = [
  'CONTAINER DRAY',
  'DELIVERY',
  'DRAY/TRANSLOAD',
  'DRAY/TRANSLOAD/DELIVERY',
];
const _WHSE_BLOCKING = new Set<string>(WHSE_BLOCKING_ITEMS);

const _whseBase = (code: unknown): string =>
  String(code ?? '').trim().toUpperCase().split('/', 1)[0];

const _whseList = (whseCodes: readonly string[] | null | undefined): string[] =>
  (whseCodes ?? []).map((c) => String(c ?? '').trim().toUpperCase()).filter(Boolean);

/** True unless `code` is a GWI code naming a warehouse that is not on the pro. */
export const whseGlCodeMatches = (
  code: string | null | undefined,
  whseCodes: readonly string[] | null | undefined,
): boolean => {
  const base = _whseBase(code);
  if (!base.startsWith('GWI')) return true;
  const list = _whseList(whseCodes);
  if (list.length === 0) return true;
  const wanted = _WHSE_PAIRED[base] ?? [base];
  return wanted.some((w) => list.includes(w));
};

/**
 * The RESOLVED-gate message(s) for a set of lines, in grid order: [] when nothing is
 * off, else ONE sentence naming the warehouse(s) and every offending line by its
 * 1-based grid position. Only `WHSE_BLOCKING_ITEMS` are scanned — everything else is
 * highlight-only. Blank and non-blocking rows are skipped but keep their position, so
 * the number names the row the grid shows. Same words as the server
 * (invoice_whse_gl.whse_gl_mismatch_message).
 */
export const whseGlCodeErrors = (
  lines: ReadonlyArray<{ item?: string | null; glCode?: string | null }>,
  whseCodes: readonly string[] | null | undefined,
): string[] => {
  const list = _whseList(whseCodes);
  if (list.length === 0) return [];
  const bad: string[] = [];
  lines.forEach((l, i) => {
    const item = String(l.item ?? '').trim();
    if (!item) return;
    if (!_WHSE_BLOCKING.has(item.toUpperCase())) return;
    const code = String(l.glCode ?? '').trim();
    if (!whseGlCodeMatches(code, list)) bad.push(`line ${i + 1} ${item} is ${code}`);
  });
  if (bad.length === 0) return [];
  const noun = list.length === 1 ? 'warehouse' : 'warehouses';
  return [
    'Pending Approval cannot be RESOLVED until the GL Code matches the Whse tab ' +
    `${noun} (${list.join(', ')}): ${bad.join(', ')}.`,
  ];
};

// Shared with the quote's Drayage & Fuel panel, which displays the same stored rate.
export { formatQuoteRate } from '../../../utils/money';

// ── staging an Invoice Estimate into an editor ────────────────────────────────
/** One change the estimate proposes, normalized away from any editor's row type.
 *
 *  Both line editors stage from the estimate — the Invoicing tab for the MAIN invoice
 *  and the Accessorial modal for its own document — and they hold a row differently
 *  (numbers vs form strings, computed total vs stored). What must NOT differ is the
 *  DECISION: which lines stage at all, and with what figures. That lives here once. */
export type StagedChange =
  | { action: 'remove'; existingRecno: number }
  | {
      action: 'add' | 'update';
      /** null on an add — there is no line to change yet. */
      existingRecno: number | null;
      item: string;
      glCode: string;
      qty: number;
      rate: number;
      rateType: string;
      description: string;
      total: number;
    };

/** The minimum of `EstimatedLine` this needs; typed structurally so the shared module
 *  does not have to import the service's full line shape. */
export interface StageableLine {
  source: string;
  existingRecno: number | null;
  item: string;
  glCode: string;
  qty: number;
  rate: number;
  rateType: string;
  description: string;
  total: number;
  applyRate: boolean;
  accessorialNum?: number;
}

/**
 * The estimate's proposed changes for ONE document, in the order they should be applied.
 *
 * `accept` picks the document — `isMainInvoiceLine` for the Invoicing tab, an
 * accessorialNum match for the Accessorial modal. Everything else is deliberately NOT
 * the caller's choice:
 *
 *  - ONLY the three proposed sources stage, written as an ALLOW-list rather than a
 *    skip-list. A skip-list is what shipped first, and when `matched` was added it fell
 *    through to the append branch and DUPLICATED every confirmed line onto the invoice.
 *    An unrecognised source must do nothing: a change that fails to stage is visible and
 *    harmless, silent duplication is neither.
 *  - `applyRate: false` means the figure is PROJECTED from an assumed date. The line
 *    stages so the user can see the charge accruing; the rate is never filled in on
 *    their behalf, so it comes through as 0 for a person to decide.
 *  - an `update` with no recno names no line, so it is dropped rather than appended —
 *    appending is the one wrong answer, because it bills the charge twice.
 */
export function estimateStagedChanges(
  lines: ReadonlyArray<StageableLine>,
  accept: (line: StageableLine) => boolean,
): StagedChange[] {
  const out: StagedChange[] = [];
  for (const line of lines) {
    if (!accept(line)) continue;
    if (line.source !== 'add' && line.source !== 'update' && line.source !== 'remove') {
      continue;
    }
    if (line.source === 'remove') {
      if (line.existingRecno != null) {
        out.push({ action: 'remove', existingRecno: line.existingRecno });
      }
      continue;
    }
    if (line.source === 'update' && line.existingRecno == null) continue;
    out.push({
      action: line.source,
      existingRecno: line.source === 'update' ? line.existingRecno : null,
      item: line.item,
      glCode: line.glCode,
      qty: line.qty,
      rate: line.applyRate ? line.rate : 0,
      rateType: line.rateType,
      description: line.description,
      total: line.applyRate ? line.total : 0,
    });
  }
  return out;
}
